/**
 * Drag-to-select-a-range state for elevation-panel-v2.
 *
 * Entry is an explicit "Select range" header button (not a freeform gesture)
 * — see the elevation-panel-v2 plan's Gesture design section for why: it
 * reuses `vue-slider-component` (already a project dependency, already
 * proven for touch range-selection in `recording_crop_app/RecordingCropApp.vue`)
 * rather than inventing new long-press/drag gesture code, and it has zero
 * disambiguation window against chart-scrub to tune (the mode switch is an
 * explicit tap, not a timer).
 *
 * Stats are computed on FULL-RESOLUTION route data (not the decimated chart
 * points) via `rangeStats.ts`, so a selection's numbers reflect the real
 * route regardless of how aggressively the chart itself downsamples for
 * rendering.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { computeRangeStats, type RangeStats } from '../../../utils/elevation/rangeStats';

export interface UseRangeSelectionOptions {
  coordinates: Ref<number[][]>;
  distancesMeters: Ref<number[]>;
  altitudes?: Ref<number[] | undefined>;
  elevationThresholdMeters?: number;
}

export interface UseRangeSelection {
  active: Ref<boolean>;
  startMeters: Ref<number | null>;
  endMeters: Ref<number | null>;
  stats: ComputedRef<RangeStats | null>;
  /** Enter range-select mode, seeding default handles at 25%/75% of the route. */
  enter(): void;
  /** Exit range-select mode and clear the selection. */
  exit(): void;
  /** Update the selection (e.g. from the VueSlider's change event), in metres. */
  setRange(startMeters: number, endMeters: number): void;
}

export function useRangeSelection(options: UseRangeSelectionOptions): UseRangeSelection {
  const active = ref(false);
  const startMeters = ref<number | null>(null);
  const endMeters = ref<number | null>(null);

  const stats = computed<RangeStats | null>(() => {
    if (startMeters.value == null || endMeters.value == null) return null;
    return computeRangeStats(
      options.coordinates.value,
      options.distancesMeters.value,
      options.altitudes?.value,
      startMeters.value,
      endMeters.value,
      options.elevationThresholdMeters
    );
  });

  function enter(): void {
    active.value = true;
    const dist = options.distancesMeters.value;
    const total = dist.length ? dist[dist.length - 1] : 0;
    startMeters.value = total * 0.25;
    endMeters.value = total * 0.75;
  }

  function exit(): void {
    active.value = false;
    startMeters.value = null;
    endMeters.value = null;
  }

  function setRange(start: number, end: number): void {
    startMeters.value = start;
    endMeters.value = end;
  }

  return { active, startMeters, endMeters, stats, enter, exit, setRange };
}
